import type { PropertySearchActions } from './actions';
import type { PropertySearchStore } from './store';
import type { EventBusKey } from '@vueuse/core';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { InjectionKey } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { hasInjectionContext, inject, provide } from 'vue';

import { createLegacySearchActions } from './legacyActions';

export type PropertyFiltersApi = Pick<
	typeof PropertyFiltersPresenter,
	| 'getPathFromFilters'
	| 'getFiltersFromPath'
	| 'getRouteObjectFromFilters'
	| 'prepareFiltersForRequest'
	| 'getAppliedFilterKeys'
	| 'createLocationFilters'
	| 'parseSlugPath'
	| 'getPropertyTypeFromSlug'
	| 'getSlugFromTransactionType'
	| 'getTransactionTypeFromSlug'
	| 'DEFAULT_URL_PATH'
	| 'DEFAULT_MAP_URL_PATH'
>;

export interface PropertySearchScope {
	filtersApi: PropertyFiltersApi;
	search: PropertySearchStore;
	actions: PropertySearchActions;
	routeName: string;
	stateKey(key: string): string;
	eventKey<T>(key: EventBusKey<T> | string): EventBusKey<T> | string;
}
const propertySearchScopeKey: InjectionKey<PropertySearchScope> = Symbol(
	'property-search-scope'
);

export function providePropertySearchScope(scope: PropertySearchScope): void {
	provide(propertySearchScopeKey, scope);
}
export function usePropertySearchScope(): PropertySearchScope | undefined {
	return hasInjectionContext()
		? inject(propertySearchScopeKey, undefined)
		: undefined;
}
export function usePropertyFilters(): PropertyFiltersApi {
	return usePropertySearchScope()?.filtersApi ?? PropertyFiltersPresenter;
}
export interface SearchUrlBuilders {
	buildSearchUrl(filters: Partial<PropertyFilters>): string;
	buildSearchRoute(filters: Partial<PropertyFilters>): RouteLocationRaw;
}
// Plain search links on shared components: the scope picks the route, so
// production keeps the presenter's exact output and the lab gets its own.
export function useSearchUrl(): SearchUrlBuilders {
	const api = usePropertyFilters();
	return {
		buildSearchUrl: filters => api.getPathFromFilters(filters),
		buildSearchRoute: filters => api.getRouteObjectFromFilters(filters)
	};
}
export function usePropertySearchStateKey(key: string): string {
	return usePropertySearchScope()?.stateKey(key) ?? key;
}
export function usePropertySearchEventKey<T>(
	key: EventBusKey<T> | string
): EventBusKey<T> | string {
	return usePropertySearchScope()?.eventKey(key) ?? key;
}

// The only place that decides between the lab and production behaviour.
export function usePropertySearchActions(): PropertySearchActions {
	const scope = usePropertySearchScope();
	if (scope) return scope.actions;
	const route = useRoute();
	return createLegacySearchActions({
		api: PropertyFiltersPresenter,
		currentUrl: () => route.fullPath,
		navigate: async to => navigateTo(to)
	});
}
